import { Component, OnDestroy } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { merge, Subscription, take } from 'rxjs';
import { EditSelectable } from '../edit-select-form-control/edit-select-form-control.component';
import { DateTimePeriod } from '../../entities/form-controls/date-time-period';
import { TimeEntry, TimeEntryInput } from '../../entities/timeEntry/timeEntry';
import { LoadingService } from '../../services/loading.service';
import { TimeEntryService } from '../../services/timeEntry/timeEntryService';
import { TimeTrackingCategoryService } from '../../services/timeTrackingCategories/timeTrackingCategories.service';
import { UsersService } from '../../services/users/users.service';
import { EditDialogComponent } from '../editDialog/editDialogComponent';
import { EditDialogInstance } from '../editDialog/editDialogService';

@Component({
  selector: 'app-time-entry-dialog',
  templateUrl: './time-entry-dialog.component.html',
  styleUrls: ['./time-entry-dialog.component.scss']
})
export class TimeEntryDialogDialogComponent
  implements EditDialogInstance, OnDestroy {

    timeEntryForm: FormGroup;
    timeEntry: TimeEntry;
    taskId: string;
    ownerId: string;
    ownerType: string;
    userId: string;
    categoryOptions: EditSelectable[] = [];
    timeOptions: string[] = this.generateTimeOptions();
    users: EditSelectable[] = [];
    currentUserId: string;
    dateTimeSubscription: Subscription;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    public timeTrackingCategoryService: TimeTrackingCategoryService,
    public loadingService: LoadingService,
    public userService: UsersService,
    public timeEntryService: TimeEntryService
  ) {

    // kategorien laden
    this.timeTrackingCategoryService.getTimeEntryCategories().pipe(
      take(1)
    ).subscribe((categories) => {
      this.categoryOptions = categories;
    });

    // users laden
    this.userService.getUsers().pipe(
      take(1)
    ).subscribe((users) => {
      this.users = users.map((user) => {
        return {
          name: user.firstName + ' ' + user.lastName,
          id: user.id,
        };
      });
    });
    console.log(this.config);
    if (this.config.data) {
      this.taskId = this.config?.data?.taskId;
      this.ownerId = this.config?.data?.ownerId;
      this.ownerType = this.config?.data?.ownerType;
      this.timeEntry = this.config?.data?.timeEntry;
    }

    this.timeEntryForm = new FormGroup({}, { validators: this.endAfterStart.bind(this) });
    this.timeEntryForm.addControl('id', new FormControl());
    // add form controls
    this.timeEntryForm.addControl('taskId', new FormControl());
    this.timeEntryForm.addControl('ownerId', new FormControl());
    this.timeEntryForm.addControl('ownerType', new FormControl());
    this.timeEntryForm.addControl('startDateTime', new FormControl(undefined, Validators.required));
    this.timeEntryForm.addControl('endDateTime', new FormControl(undefined, Validators.required));
    this.timeEntryForm.addControl('userId', new FormControl(undefined, Validators.required));
    this.timeEntryForm.addControl('pauseDuration', new FormControl(undefined, [this.validatePauseDuration.bind(this)]));
    // pauseDuration erst aktivieren wenn start- und endzeit gesetzt sind
    this.timeEntryForm.get('pauseDuration')?.disable();
    this.dateTimeSubscription = merge(
      this.timeEntryForm.get('startDateTime')!.valueChanges,
      this.timeEntryForm.get('endDateTime')!.valueChanges,
    ).subscribe(() => {
      const startValue = this.timeEntryForm.get('startDateTime')?.value;
      const endValue = this.timeEntryForm.get('endDateTime')?.value;
      if (startValue?.dateTime && endValue?.dateTime) {
        this.timeEntryForm.get('pauseDuration')?.enable();
        // Validierung neu auslösen wenn sich Zeiten ändern
        this.timeEntryForm.get('pauseDuration')?.updateValueAndValidity();
      } else {
        this.timeEntryForm.get('pauseDuration')?.disable();
      }
    });
    this.timeEntryForm.addControl('categoryId', new FormControl(undefined, Validators.required));
    this.timeEntryForm.addControl('note', new FormControl());

    if (this.timeEntry) {
      this.timeEntryForm.patchValue({
        id: this.timeEntry.id,
        taskId: this.timeEntry.taskId,
        userId: this.timeEntry.user?.id,
        categoryId: this.timeEntry.category?.id,
        note: this.timeEntry.note,
        pauseDuration: this.timeEntry.pauseDuration ? this.convertSecondsToTime(this.timeEntry.pauseDuration) : undefined,
        startDateTime: this.buildSingleDateTime(this.timeEntry.startDateTime),
        endDateTime: this.buildSingleDateTime(this.timeEntry.endDateTime),
      });
    } else {
      // prefill form
      this.timeEntryForm.patchValue({
        taskId: this.taskId,
        ownerId: this.ownerId,
        ownerType: this.ownerType,
        userId: this.userService.getCurrentUserId(),
      });
    }
  }

  ngOnDestroy(): void {
    this.dateTimeSubscription?.unsubscribe();
  }

  buildSingleDateTime(isoString: string): DateTimePeriod | undefined {
    if (!isoString) return undefined;
    return new DateTimePeriod({
      dateTime: isoString,
      modifier: 'EXACT',
      precision: 'DATE_TIME',
    });
  }


  onConfirm(editDialogComponent: EditDialogComponent<any>): void {
    this.loadingService.setLoading(true);
    // build the time entry input, we do it manually here and not 
    // in the timeEntryForm.value because we need to convert the date
    let timeEntryInput = new TimeEntryInput();
    timeEntryInput.id = this.timeEntryForm.get('id')?.value;
    timeEntryInput.taskId = this.timeEntryForm.get('taskId')?.value;
    timeEntryInput.ownerId = this.timeEntryForm.get('ownerId')?.value;
    timeEntryInput.ownerType = this.timeEntryForm.get('ownerType')?.value;
    timeEntryInput.userId = this.timeEntryForm.get('userId')?.value;
    timeEntryInput.note = this.timeEntryForm.get('note')?.value;
    timeEntryInput.categoryId = this.timeEntryForm.get('categoryId')?.value;
    // fields that need to be converted
    timeEntryInput.startDateTime = this.timeEntryForm.get('startDateTime')?.value?.dateTime;
    timeEntryInput.endDateTime = this.timeEntryForm.get('endDateTime')?.value?.dateTime;
    timeEntryInput.pauseDuration = this.convertTimeToSeconds(this.timeEntryForm.get('pauseDuration')?.value);

    if(this.timeEntry){
      this.timeEntryService.updateTimeEntry(timeEntryInput, (result: any) => {
        this.loadingService.setLoading(false);
        this.ref.close(result?.data?.updateTimeEntry?.id);
      });
    } else {
      this.timeEntryService.createTimeEntry(timeEntryInput, (result: any) => {
        this.loadingService.setLoading(false);
        this.ref.close(result?.data?.createTimeEntry?.id);
      });
    }
  }

  convertTimeToSeconds(timeString: string): number {
    if (!timeString) {
      return 0;
    }
    // Split the string by colon
    const [hours, minutes] = timeString.split(':').map(part => parseInt(part, 10));
    
    // Validate input
    if (isNaN(hours) || isNaN(minutes)) {
      throw new Error('Invalid time format. Expected "hh:mm"');
    }
    
    // Calculate total minutes: (hours * 60) + minutes
    return (hours * 3600) + (minutes * 60);
  }

  convertSecondsToTime(seconds: number): string {
    if (seconds < 0) {
      throw new Error('Seconds cannot be negative');
    }
  
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
  
    const hoursStr = hours.toString().padStart(2, '0');
    const minutesStr = minutes.toString().padStart(2, '0');
  
    return `${hoursStr}:${minutesStr}`;
  }

  // Cross-field validator: Endzeit muss nach Startzeit liegen
  endAfterStart(group: AbstractControl): ValidationErrors | null {
    const startValue = group.get('startDateTime')?.value;
    const endValue = group.get('endDateTime')?.value;
    if (!startValue?.dateTime || !endValue?.dateTime) {
      return null;
    }
    const start = new Date(startValue.dateTime).getTime();
    const end = new Date(endValue.dateTime).getTime();
    return end <= start ? { endNotAfterStart: true } : null;
  }

  getEndDateTimeError(): string | undefined {
    if (this.timeEntryForm.hasError('endNotAfterStart')) {
      return 'Endzeit muss nach der Startzeit liegen.';
    }
    return undefined;
  }

  // Custom validator to check if pauseDuration is valid
  validatePauseDuration(control: AbstractControl): ValidationErrors | null {
    const pauseDurationStr = control.value;
    if (!pauseDurationStr) {
      return null; // Empty value is valid (not required)
    }
    
    const startValue = this.timeEntryForm?.get('startDateTime')?.value;
    const endValue = this.timeEntryForm?.get('endDateTime')?.value;
    if (!startValue?.dateTime || !endValue?.dateTime) {
      return null; // Can't validate if start/end times not set
    }
    
    // Calculate time difference in seconds
    const startTime = new Date(startValue.dateTime).getTime() / 1000;
    const endTime = new Date(endValue.dateTime).getTime() / 1000;
    const totalDurationSeconds = endTime - startTime;
    
    // Convert pauseDuration to seconds
    const pauseDurationSeconds = this.convertTimeToSeconds(pauseDurationStr);
    
    // Compare if pause duration is longer than total duration
    if (pauseDurationSeconds >= totalDurationSeconds) {
      return { 'pauseTooLong': true };
    }
    
    return null;
  }

  onReject(editDialogComponent: EditDialogComponent<any>): void {
    this.ref.close(false);
  }

  getEditForm(): FormGroup {
    return this.timeEntryForm;
  }

  generateTimeOptions(): string[] {
    const options: string[] = [];
    for (let hour = 0; hour < 8; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const hourStr = hour.toString().padStart(2, '0');
        const minuteStr = minute.toString().padStart(2, '0');
        const timeStr = `${hourStr}:${minuteStr}`;
        options.push(timeStr);
      }
    }
    return options;
  }

  getPauseError(): string {
    const control = this.timeEntryForm.get('pauseDuration');
    if (!control) {
      return undefined;
    }
    
    if (control.hasError('pauseTooLong')) {
      return 'Pausendauer darf nicht länger als die Gesamtdauer sein.';
    }
    
    return undefined;
  }

  onTimeFocus(event: any) {
    event.target.select();
  }

  onTimeBlur(event: any, formControlName: string) {
    // let value = this.timeEntryForm.get(formControlName)?.value;
    // if (!value) return;
    
    // // Handle numeric input like "1300"
    // if (/^\d+$/.test(value)) {
    //     const timeString = value.padStart(4, '0');
    //     const hours = timeString.substring(0, 2);
    //     const minutes = timeString.substring(2, 4);
        
    //     // Validate hours and minutes
    //     if (parseInt(hours) < 24 && parseInt(minutes) < 60) {
    //     value = `${hours}:${minutes}`;
    //     }
    //     this.timeEntryForm.get(formControlName)?.setValue(value);
    // }
  }
}
